import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Send,
  Paperclip,
  FileText,
  Check,
  CheckCheck,
  Loader2,
  User,
  MessageSquare
} from 'lucide-react';
import { chatAPI } from '../../services/api';
import {
  initSocket,
  joinChatRoom,
  leaveChatRoom,
  onNewChatMessage,
  offNewChatMessage,
  onUserTyping,
  offUserTyping,
  emitTypingStart,
  emitTypingStop
} from '../../services/socket';
import FileUpload from '../shared/FileUpload';
import { toast } from 'sonner';
import { format, isToday, isYesterday } from 'date-fns';

export default function ServiceChat({ serviceRequestId, counselor, agent, isStaffView = false, readOnly = false }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [chatDisabled, setChatDisabled] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const data = await chatAPI.getMessages(serviceRequestId);
      setMessages(data.messages || []);
      setChatDisabled(false);

      // Mark all as read
      await chatAPI.markAllAsRead(serviceRequestId);
    } catch (error) {
      if (error.response?.data?.chatDisabled) {
        setChatDisabled(true);
      } else {
        console.error('Failed to fetch messages:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [serviceRequestId]);

  // Initialize socket and fetch messages
  useEffect(() => {
    fetchMessages();
    initSocket();
    joinChatRoom(serviceRequestId);

    // Listen for new messages
    onNewChatMessage(({ serviceRequestId: incomingId, message }) => {
      if (incomingId === serviceRequestId) {
        setMessages(prev => [...prev, message]);
        // Mark as read immediately since user is viewing
        chatAPI.markAsRead(serviceRequestId, message.messageId).catch(() => {});
      }
    });

    // Listen for typing indicators
    onUserTyping(({ userId, isTyping }) => {
      if (userId !== currentUser.userId) {
        setTypingUsers(prev => {
          if (isTyping) {
            return prev.includes(userId) ? prev : [...prev, userId];
          } else {
            return prev.filter(id => id !== userId);
          }
        });
      }
    });

    return () => {
      leaveChatRoom(serviceRequestId);
      offNewChatMessage();
      offUserTyping();
    };
  }, [serviceRequestId, fetchMessages, currentUser.userId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle typing indicator
  const handleTyping = () => {
    emitTypingStart(serviceRequestId);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      emitTypingStop(serviceRequestId);
    }, 1000);
  };

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    setSending(true);
    emitTypingStop(serviceRequestId);

    try {
      const messageType = attachments.length > 0 ? 'FILE' : 'TEXT';
      const data = await chatAPI.sendMessage(
        serviceRequestId,
        newMessage,
        attachments,
        messageType
      );

      // Add message to local state
      setMessages(prev => [...prev, data.message]);
      setNewMessage('');
      setAttachments([]);
      setShowAttachments(false);
      inputRef.current?.focus();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (uploadedFiles) => {
    setAttachments(prev => [...prev, ...uploadedFiles]);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format message date
  const formatMessageDate = (date) => {
    const d = new Date(date);
    if (isToday(d)) return format(d, 'h:mm a');
    if (isYesterday(d)) return `Yesterday ${format(d, 'h:mm a')}`;
    return format(d, 'MMM d, h:mm a');
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = format(new Date(message.createdAt), 'yyyy-MM-dd');
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  // Get recipient info
  const recipient = isStaffView ? null : (counselor || agent);

  // If chat is disabled (no counselor assigned)
  if (chatDisabled || (!isStaffView && !recipient)) {
    return (
      <Card className="p-8 text-center">
        <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500 font-medium">Chat Not Available</p>
        <p className="text-sm text-gray-400 mt-1">
          Chat will be available once a counselor is assigned to your service request.
        </p>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-[500px]">
      {/* Chat Header */}
      {!isStaffView && recipient && (
        <div className="p-4 border-b flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
            {recipient.firstName?.[0]}{recipient.lastName?.[0]}
          </div>
          <div className="flex-1">
            <p className="font-semibold">{recipient.firstName} {recipient.lastName}</p>
            <p className="text-sm text-gray-500">
              {counselor ? 'Counselor' : 'Agent'}
            </p>
          </div>
          <Badge className="bg-green-100 text-green-700">Online</Badge>
        </div>
      )}

      {isStaffView && (
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            <span className="font-semibold">Student Chat</span>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center">
            <div>
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 mb-2">No messages yet</p>
              <p className="text-sm text-gray-400">
                {isStaffView ? 'Start the conversation with your student' : 'Start the conversation with your counselor'}
              </p>
            </div>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date Separator */}
              <div className="flex items-center justify-center my-4">
                <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                  {isToday(new Date(date)) ? 'Today' :
                   isYesterday(new Date(date)) ? 'Yesterday' :
                   format(new Date(date), 'MMMM d, yyyy')}
                </div>
              </div>

              {/* Messages for this date */}
              {dateMessages.map((message) => {
                const isOwn = message.senderId === currentUser.userId;
                const isRead = message.readBy?.some(r => r.userId !== currentUser.userId);

                return (
                  <div
                    key={message.messageId}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}
                  >
                    <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                      {/* Sender Avatar (for received messages) */}
                      {!isOwn && (
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                            {message.sender?.firstName?.[0]}
                          </div>
                          <span className="text-xs text-gray-500">
                            {message.sender?.firstName}
                          </span>
                        </div>
                      )}

                      {/* Message Bubble */}
                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          isOwn
                            ? 'bg-blue-500 text-white rounded-br-md'
                            : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                        {/* Attachments */}
                        {message.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {message.attachments.map((file, idx) => (
                              <a
                                key={idx}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-xs ${
                                  isOwn ? 'text-blue-100 hover:text-white' : 'text-blue-500 hover:text-blue-700'
                                }`}
                              >
                                <FileText className="w-3 h-3" />
                                {file.name}
                              </a>
                            ))}
                          </div>
                        )}

                        {/* Timestamp and Read Status */}
                        <div className={`flex items-center gap-1 mt-1 ${
                          isOwn ? 'justify-end' : 'justify-start'
                        }`}>
                          <span className={`text-xs ${isOwn ? 'text-blue-100' : 'text-gray-400'}`}>
                            {formatMessageDate(message.createdAt)}
                          </span>
                          {isOwn && (
                            isRead ? (
                              <CheckCheck className="w-3 h-3 text-blue-100" />
                            ) : (
                              <Check className="w-3 h-3 text-blue-200" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* Typing Indicator */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>Someone is typing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 border-t bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, idx) => (
              <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {file.name}
                <button
                  onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                  className="ml-1 hover:text-red-500"
                >
                  x
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      {readOnly ? (
        <div className="p-3 border-t bg-gray-50 text-center">
          <p className="text-xs text-gray-400">Read-only view — you can follow this conversation but cannot send messages</p>
        </div>
      ) : (
        <div className="p-4 border-t">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowAttachments(!showAttachments)}
            >
              <Paperclip className="w-5 h-5" />
            </Button>

            <Input
              ref={inputRef}
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onKeyPress={handleKeyPress}
              className="flex-1"
            />

            <Button
              onClick={handleSend}
              disabled={sending || (!newMessage.trim() && attachments.length === 0)}
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>

          {/* File Upload (conditional) */}
          {showAttachments && (
            <div className="mt-3">
              <FileUpload
                onUploadComplete={handleFileUpload}
                maxFiles={5}
                folder="chat-attachments"
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
